import { memo, useCallback, useState } from 'react';

import { ChevronDownIcon, SquareStackIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { SelectDropdownMenu, type SelectDropdownMenuOption } from '@air/primitive-dropdown-menu';
import { cn } from '@air/utils-tailwind';

import { VARIANT_COUNTS, type VariantCount } from '../../../types/subscriptions';

const OPTIONS: SelectDropdownMenuOption[] = VARIANT_COUNTS.map((count) => ({
  canSelect: true,
  id: String(count),
  label: String(count),
  type: 'item',
}));

export type VariantSelectorProps = {
  disabled?: boolean;
  onChange: (count: VariantCount) => void;
  onOpenChange?: (open: boolean) => void;
  value: VariantCount;
};

export const VariantSelector = memo(({ disabled, onChange, onOpenChange, value }: VariantSelectorProps) => {
  const [open, setOpen] = useState(false);

  const handleSelect = useCallback(
    (option: { id: string }) => {
      const count = VARIANT_COUNTS.find((c) => String(c) === option.id);

      if (count) {
        onChange(count);
      }
    },
    [onChange],
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  return (
    <SelectDropdownMenu
      onOpenChange={handleOpenChange}
      onSelect={handleSelect}
      open={open}
      options={OPTIONS}
      selectedOption={{ id: String(value) }}
      side="top"
      sideOffset={8}
      trigger={
        <Button
          appearance="outline"
          aria-label={`Variants: ${value}`}
          color="gray"
          disabled={disabled}
          prefix={<SquareStackIcon className="shrink-0" size="xs" />}
          size="small"
          suffix={
            <ChevronDownIcon
              className={cn('shrink-0 text-gray-9 transition-transform', open && 'rotate-180')}
              size="xs"
            />
          }
          type="button"
        >
          {value}
        </Button>
      }
      triggerTooltip="Number of variants"
    />
  );
});

VariantSelector.displayName = 'VariantSelector';
